import { useEffect, useState, useCallback } from "react";
import { Receipt, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINRFull, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const empty = { date: "", description: "", amount: "", cgst_pct: "9", sgst_pct: "9", status: "Draft", notes: "" };

export function BillingPanel({ projectId, quotedValue, onChanged }) {
  const { meta } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/projects/${projectId}/invoices`); setInvoices(data); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...empty, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (i) => { setEditing(i); setForm({ date: (i.date || "").slice(0, 10), description: i.description || "", amount: i.amount ?? "", cgst_pct: String(i.cgst_pct ?? 9), sgst_pct: String(i.sgst_pct ?? 9), status: i.status, notes: i.notes || "" }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const amt = Number(form.amount) || 0;
  const cgst = Number(form.cgst_pct) || 0;
  const sgst = Number(form.sgst_pct) || 0;
  const cgstAmt = Math.round(amt * cgst) / 100;
  const sgstAmt = Math.round(amt * sgst) / 100;
  const gstAmt = cgstAmt + sgstAmt;
  const total = amt + gstAmt;

  const submit = async () => {
    if (amt <= 0) { toast.error("Invoice amount must be greater than 0"); return; }
    setSaving(true);
    const payload = { date: form.date, description: form.description, amount: amt, cgst_pct: cgst, sgst_pct: sgst, status: form.status, notes: form.notes };
    try {
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/invoices`, payload);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setOpen(false); load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => { await api.delete(`/invoices/${del.id}`); toast.success("Invoice deleted"); setDel(null); load(); onChanged && onChanged(); };

  const invoicedTotal = invoices.reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-4" data-testid="billing-panel">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <SummaryCard label="Quoted Value" value={formatINRFull(quotedValue)} />
        <SummaryCard label="Total Invoiced" value={formatINRFull(invoicedTotal)} color="#0057A8" />
        <SummaryCard label="Invoices" value={String(invoices.length)} />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Receipt className="h-4 w-4 text-[#0057A8]" /><h3 className="text-base font-semibold text-slate-800">Invoices</h3></div>
        <Button data-testid="add-invoice-btn" onClick={openNew} size="sm" className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> New Invoice</Button>
      </div>

      {loading ? (
        <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
      ) : invoices.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center">
          <p className="text-sm font-semibold text-slate-700">No invoices yet</p>
          <p className="mt-1 text-xs text-slate-400">Raise an invoice against this project to start billing.</p>
          <Button onClick={openNew} className="mt-4 gap-1.5"><Plus className="h-4 w-4" /> Create First Invoice</Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Date</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">CGST</th><th className="px-4 py-3 text-right">SGST</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="border-b border-slate-50">
                  <td className="px-4 py-3"><div className="font-mono font-semibold text-slate-800">{i.invoice_no}</div>{i.description && <div className="text-xs text-slate-400">{i.description}</div>}</td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(i.date)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{formatINRFull(i.amount)}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">{i.cgst_pct ?? 0}% · {formatINRFull(i.cgst_amount ?? 0)}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">{i.sgst_pct ?? 0}% · {formatINRFull(i.sgst_amount ?? 0)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{formatINRFull(i.total)}</td>
                  <td className="px-4 py-3"><StatusBadge value={i.status} type="invoice" dot /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(i)} data-testid={`edit-invoice-${i.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDel(i)} data-testid={`delete-invoice-${i.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={5} className="px-4 py-3 text-right text-sm font-semibold text-slate-500">Total Invoiced</td><td className="px-4 py-3 text-right font-mono text-base font-extrabold text-slate-900">{formatINRFull(invoicedTotal)}</td><td colSpan={2}></td></tr></tfoot>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.invoice_no}` : "New Invoice"}</DialogTitle><DialogDescription>Enter the taxable amount and GST %; the total is calculated automatically.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Invoice Date</Label><Input data-testid="inv-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="inv-status"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.invoice_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Description</Label><Input data-testid="inv-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. 1st milestone — 50% advance" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Amount (₹, before GST)</Label><Input data-testid="inv-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">CGST %</Label><Input data-testid="inv-cgst" type="number" value={form.cgst_pct} onChange={(e) => set("cgst_pct", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">SGST %</Label><Input data-testid="inv-sgst" type="number" value={form.sgst_pct} onChange={(e) => set("sgst_pct", e.target.value)} /></div>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <div className="mt-1 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 text-sm">
            <span className="text-slate-500">CGST {cgst}%: <span className="font-mono font-medium text-slate-700">{formatINRFull(cgstAmt)}</span> · SGST {sgst}%: <span className="font-mono font-medium text-slate-700">{formatINRFull(sgstAmt)}</span></span>
            <span className="font-semibold text-slate-800">Total: <span data-testid="inv-total-preview" className="font-mono text-base font-extrabold text-slate-900">{formatINRFull(total)}</span></span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="inv-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Saving…" : editing ? "Save Invoice" : "Create Invoice"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Delete invoice?" description="This removes the invoice. Linked payments will be unlinked but not deleted." confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}

function SummaryCard({ label, value, color = "#102A43" }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 font-mono text-base font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
